import { faSort, faSortDown, faSortUp } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  type ColumnDef,
  type ColumnFiltersState,
  type ColumnOrderState,
  type ColumnPinningState,
  type ColumnSizingState,
  type ColumnVisibilityState,
  type ExpandedState,
  flexRender,
  type OnChangeFn,
  type PaginationState,
  type Row,
  type RowData,
  type RowSelectionState,
  type SortingState,
  useTable
} from '@tanstack/react-table'
import clsx from 'clsx'
import { AnimatePresence } from 'motion/react'
import {
  Fragment,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { checkboxStyle } from '~/components/Form/Input/Checkbox.css'
import ActionBar from '~/components/Global/Blocks/ActionBar/ActionBar'
import { LoaderIcon } from '~/components/Global/Elements/Loader/LoaderIcon'
import { NoResult } from '~/components/Global/Elements/NoResult/NoResult'
import { Shimmer } from '~/components/Global/Elements/Shimmer/Shimmer'
import { useUpdateConfig } from '~/domains/configuration/mutations/updateConfig'
import { useConfig } from '~/domains/configuration/queries/config'
import type {
  ConfigKeyMap,
  ConfigName,
  ConfigTable
} from '~/domains/configuration/types'
import { useDragToScroll } from '~/hooks/useDragToScroll'
import { themeClass } from '~/styles/theme.css'
import {
  cellAlignCenter,
  cellAlignRight,
  cellMono,
  checkboxCell,
  dataTableRoot,
  emptyStateOverlay,
  expandedRow,
  expandedRowCell,
  expandedRowContent,
  resizeHandle,
  resizeHandleActive,
  rowActionsBar,
  rowActionsCell,
  stickyCell,
  stickyCellEdge,
  stickyCellRight,
  stickyCellRightEdge,
  stickyHeaderBar,
  stickyHeaderInner,
  table as tableClass,
  tableFrame,
  tableLoadingOverlay,
  tableWrapper,
  tableWrapperPanning,
  tbody,
  tdCell,
  thCell,
  thead,
  theadTitle,
  thFilterCell,
  thSortable,
  thSortIndicator,
  tr,
  trClickable,
  trSelected
} from './DataTable.css'
import DataTableColumnPicker, {
  type ColumnPickerZone,
  type DataTableColumnPickerItem,
  type DataTableColumnPin
} from './DataTableColumnPicker'
import DataTableHeaderFilter from './DataTableHeaderFilter'
import DataTablePagination from './DataTablePagination'
import DataTableStickyScrollbar from './DataTableStickyScrollbar'
import { type DataTableFeatures, dataTableFeatures } from './dataTableFeatures'
import {
  DATA_TABLE_DEFAULT_COLUMN_SIZE,
  type DataTableQueryParams,
  type DataTableRowSelection,
  type DataTableSort
} from './types'
import { useHorizontalScrollSync } from './useHorizontalScrollSync'

type TableConfigName = {
  [K in ConfigName]: NonNullable<ConfigKeyMap[K]> extends ConfigTable
    ? K
    : never
}[ConfigName]

type DataTableProps<T extends RowData> = {
  columns: ColumnDef<DataTableFeatures, T, unknown>[]
  data: T[]
  totalItems: number
  queryParams: DataTableQueryParams
  defaultSorting?: DataTableSort[]
  onQueryChange: (params: DataTableQueryParams) => void
  rowSelection?: DataTableRowSelection
  onRowSelectionChange?: (selection: DataTableRowSelection) => void
  getRowId?: (row: T) => string
  onRowClick?: (row: T) => void
  rowActions?: (row: T) => ReactNode
  bulkActions?: (selectedIds: string[]) => ReactNode
  renderExpandedRow?: (row: T) => ReactNode
  isLoading?: boolean
  isFetching?: boolean
  onRefresh?: () => void
  onResetFilters?: () => void
  showResetFilters?: boolean
  onColumnFilterClick?: (columnId: string, value: string) => void
  configName?: TableConfigName
}

const DEFAULT_COLUMN_MIN = 48
const DEFAULT_COLUMN_MAX = 640
const SELECT_COLUMN_ID = '__select'
const SKELETON_ROWS_COUNT = 8
const SKELETON_CELL_WIDTHS = ['70%', '45%', '85%', '60%', '35%']

const cellAlignClass = (
  align: 'left' | 'center' | 'right' | undefined
): string => {
  if (align === 'center') return cellAlignCenter
  if (align === 'right') return cellAlignRight
  return ''
}

const getColumnLabel = <T extends RowData>(
  column: ColumnDef<DataTableFeatures, T, unknown>
): string => {
  if (column.meta?.label) return column.meta.label
  if (typeof column.header === 'string' && column.header.trim() !== '') {
    return column.header
  }
  return column.id ?? ''
}

type ColumnZones = {
  left: string[]
  middle: string[]
  right: string[]
}

const partitionColumnZones = (
  order: string[],
  dataColumnIds: string[],
  pinnedLeft: string[],
  pinnedRight: string[]
): ColumnZones => {
  const left = pinnedLeft.filter((id) => dataColumnIds.includes(id))
  const right = pinnedRight.filter((id) => dataColumnIds.includes(id))
  const pinned = new Set([...left, ...right])
  const dataOrder = order.filter(
    (id) => id !== SELECT_COLUMN_ID && dataColumnIds.includes(id)
  )
  const middleKnown = dataOrder.filter((id) => !pinned.has(id))
  const missing = dataColumnIds.filter(
    (id) => !middleKnown.includes(id) && !pinned.has(id)
  )
  return {
    left,
    middle: [...middleKnown, ...missing],
    right
  }
}

const normalizeColumnOrder = (
  order: string[],
  dataColumnIds: string[],
  hasSelect: boolean,
  pinnedLeft: string[] = [],
  pinnedRight: string[] = []
): ColumnOrderState => {
  const { left, middle, right } = partitionColumnZones(
    order,
    dataColumnIds,
    pinnedLeft,
    pinnedRight
  )
  const dataOrder = [...left, ...middle, ...right]
  return hasSelect ? [SELECT_COLUMN_ID, ...dataOrder] : dataOrder
}

const defaultPinsFromColumns = <T extends RowData>(
  columns: ColumnDef<DataTableFeatures, T, unknown>[]
): { left: string[]; right: string[] } => {
  const left: string[] = []
  const right: string[] = []
  for (const col of columns) {
    if (!col.id) continue
    if (col.meta?.stickyRight === true) {
      right.push(col.id)
    } else if (col.meta?.sticky === true) {
      left.push(col.id)
    }
  }
  return { left, right }
}

const serializeFilters = (filters: ColumnFiltersState): string =>
  JSON.stringify(
    filters
      .map((f) => ({ id: f.id, value: f.value }))
      .sort((a, b) => a.id.localeCompare(b.id))
  )

const DataTable = <T extends RowData>({
  columns,
  data,
  totalItems,
  queryParams,
  defaultSorting,
  onQueryChange,
  rowSelection = {},
  onRowSelectionChange,
  getRowId,
  onRowClick,
  rowActions,
  bulkActions,
  renderExpandedRow,
  isLoading = false,
  isFetching = false,
  onRefresh,
  onResetFilters,
  showResetFilters,
  onColumnFilterClick,
  configName
}: DataTableProps<T>): ReactElement => {
  const { data: tableConfig } = useConfig(configName)
  const { mutate: saveConfig } = useUpdateConfig(configName)

  const tableWrapperRef = useRef<HTMLDivElement>(null)
  const headerScrollRef = useRef<HTMLDivElement>(null)
  const stickyScrollbarRef = useRef<HTMLDivElement>(null)
  const { isPanning } = useDragToScroll(tableWrapperRef)
  const [scrollbarVisible, setScrollbarVisible] = useState(false)

  useHorizontalScrollSync(
    [tableWrapperRef, headerScrollRef, stickyScrollbarRef],
    scrollbarVisible
  )

  const configApplied = useRef(false)
  const configRef = useRef(tableConfig)
  configRef.current = tableConfig

  const sorting: SortingState = queryParams.sorting
  const pagination: PaginationState = useMemo(
    () => ({
      pageIndex: Math.max(0, queryParams.page - 1),
      pageSize: queryParams.itemsPerPage
    }),
    [queryParams.page, queryParams.itemsPerPage]
  )

  const hasSelectColumn = !!onRowSelectionChange

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    queryParams.columnFilters
  )
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({})
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([])
  const [columnVisibility, setColumnVisibility] =
    useState<ColumnVisibilityState>({})
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(
    () => ({
      start: hasSelectColumn ? [SELECT_COLUMN_ID] : [],
      end: []
    })
  )
  const columnPinningRef = useRef(columnPinning)
  columnPinningRef.current = columnPinning
  const [expanded, setExpanded] = useState<ExpandedState>({})

  const pinnedLeft = useMemo(
    () => columnPinning.start.filter((id) => id !== SELECT_COLUMN_ID),
    [columnPinning.start]
  )
  const pinnedRight = useMemo(() => columnPinning.end, [columnPinning.end])

  const setDataColumnPinning = useCallback(
    (left: string[], right: string[]) => {
      setColumnPinning({
        start: hasSelectColumn ? [SELECT_COLUMN_ID, ...left] : left,
        end: right
      })
    },
    [hasSelectColumn]
  )

  const filtersSyncKey = serializeFilters(queryParams.columnFilters)
  const columnFiltersFromQueryRef = useRef(queryParams.columnFilters)
  columnFiltersFromQueryRef.current = queryParams.columnFilters
  // biome-ignore lint/correctness/useExhaustiveDependencies: filtersSyncKey is the semantic dependency; value is read via ref
  useEffect(() => {
    setColumnFilters(columnFiltersFromQueryRef.current)
  }, [filtersSyncKey])

  const emitQuery = useCallback(
    (s: SortingState, f: ColumnFiltersState, p: PaginationState) => {
      onQueryChange({
        page: p.pageIndex + 1,
        itemsPerPage: p.pageSize,
        sorting: s,
        columnFilters: f.map((filter) => ({
          id: filter.id,
          value: filter.value
        }))
      })
    },
    [onQueryChange]
  )

  const dataColumnIds = useMemo(
    () => columns.map((col) => col.id).filter((id): id is string => !!id),
    [columns]
  )

  useEffect(() => {
    if (configApplied.current || (configName && tableConfig === undefined)) {
      return
    }
    configApplied.current = true
    const cfg: Partial<ConfigTable> = tableConfig ?? {}
    const defaults = defaultPinsFromColumns(columns)

    if (cfg.columnSizes) {
      setColumnSizing(cfg.columnSizes)
    }
    if (cfg.columnVisibility) {
      setColumnVisibility(cfg.columnVisibility)
    }

    const nextLeft = cfg.pinnedLeft ?? defaults.left
    const nextRight = cfg.pinnedRight ?? defaults.right
    setDataColumnPinning(nextLeft, nextRight)

    const nextOrder = normalizeColumnOrder(
      cfg.columnOrder ?? dataColumnIds,
      dataColumnIds,
      hasSelectColumn,
      nextLeft,
      nextRight
    )
    setColumnOrder(nextOrder)

    if (cfg.itemsPerPage && typeof cfg.itemsPerPage === 'number') {
      if (cfg.itemsPerPage !== queryParams.itemsPerPage) {
        emitQuery(sorting, columnFilters, {
          pageIndex: pagination.pageIndex,
          pageSize: cfg.itemsPerPage
        })
      }
    }
  }, [
    tableConfig,
    configName,
    columns,
    dataColumnIds,
    hasSelectColumn,
    setDataColumnPinning,
    emitQuery,
    sorting,
    columnFilters,
    queryParams.itemsPerPage,
    pagination.pageIndex
  ])

  useEffect(() => {
    if (!configApplied.current) return
    setColumnOrder((prev) =>
      normalizeColumnOrder(
        prev.length === 0 ? dataColumnIds : prev,
        dataColumnIds,
        hasSelectColumn,
        columnPinningRef.current.start.filter((id) => id !== SELECT_COLUMN_ID),
        columnPinningRef.current.end
      )
    )
  }, [dataColumnIds, hasSelectColumn])

  const saveConfigValue = useCallback(
    (patch: Partial<ConfigTable>) => {
      if (!configName) return
      const current =
        configRef.current && typeof configRef.current === 'object'
          ? configRef.current
          : {}
      saveConfig({ value: { ...current, ...patch } })
    },
    [configName, saveConfig]
  )

  const debouncedSaveColumnSizing = useDebouncedCallback(
    (next: ColumnSizingState) => saveConfigValue({ columnSizes: next }),
    800
  )

  const handleColumnSizingChange: OnChangeFn<ColumnSizingState> = (updater) => {
    setColumnSizing((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      debouncedSaveColumnSizing(next)
      return next
    })
  }

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater
      emitQuery(next, columnFilters, { ...pagination, pageIndex: 0 })
    },
    [emitQuery, sorting, columnFilters, pagination]
  )

  const debouncedFilterEmit = useDebouncedCallback(
    (
      filters: ColumnFiltersState,
      resetPagination: PaginationState,
      currentSorting: SortingState
    ) => {
      emitQuery(currentSorting, filters, resetPagination)
    },
    300
  )

  const toggleColumnSort = useCallback(
    (columnId: string) => {
      const current = sorting.find((s) => s.id === columnId)
      let next: SortingState
      if (!current) {
        next = [{ id: columnId, desc: false }]
      } else if (!current.desc) {
        next = [{ id: columnId, desc: true }]
      } else {
        next = defaultSorting ?? []
      }
      emitQuery(next, columnFilters, { ...pagination, pageIndex: 0 })
    },
    [defaultSorting, sorting, columnFilters, pagination, emitQuery]
  )

  const handleColumnFiltersChange: OnChangeFn<ColumnFiltersState> = useCallback(
    (updater) => {
      const next =
        typeof updater === 'function' ? updater(columnFilters) : updater
      const resetPagination = { ...pagination, pageIndex: 0 }
      setColumnFilters(next)
      debouncedFilterEmit(next, resetPagination, sorting)
    },
    [columnFilters, pagination, sorting, debouncedFilterEmit]
  )

  const handlePaginationChange: OnChangeFn<PaginationState> = useCallback(
    (updater) => {
      const next = typeof updater === 'function' ? updater(pagination) : updater
      if (next.pageSize !== pagination.pageSize) {
        saveConfigValue({ itemsPerPage: next.pageSize })
      }
      emitQuery(sorting, columnFilters, next)
    },
    [emitQuery, saveConfigValue, pagination, sorting, columnFilters]
  )

  const handleResetFilters = useCallback(() => {
    setColumnFilters([])
    if (onResetFilters) {
      onResetFilters()
      return
    }
    emitQuery(sorting, [], { ...pagination, pageIndex: 0 })
  }, [emitQuery, onResetFilters, sorting, pagination])

  const handleColumnFilterClick = useCallback(
    (columnId: string, value: string) => {
      const next = [
        ...columnFilters.filter((f) => f.id !== columnId),
        { id: columnId, value }
      ]
      setColumnFilters(next)
      emitQuery(sorting, next, { ...pagination, pageIndex: 0 })
      onColumnFilterClick?.(columnId, value)
    },
    [emitQuery, onColumnFilterClick, columnFilters, pagination, sorting]
  )

  const debouncedPersistColumnLayout = useDebouncedCallback(
    (
      order: ColumnOrderState,
      visibility: ColumnVisibilityState,
      left: string[],
      right: string[]
    ) => {
      saveConfigValue({
        columnOrder: order.filter((id) => id !== SELECT_COLUMN_ID),
        columnVisibility: visibility,
        pinnedLeft: left,
        pinnedRight: right
      })
    },
    400
  )

  const persistColumnLayout = useCallback(
    (
      order: ColumnOrderState,
      visibility: ColumnVisibilityState,
      left: string[],
      right: string[]
    ) => {
      debouncedPersistColumnLayout(order, visibility, left, right)
    },
    [debouncedPersistColumnLayout]
  )

  const splitZones = useCallback(
    (order: ColumnOrderState, left: string[], right: string[]) =>
      partitionColumnZones(order, dataColumnIds, left, right),
    [dataColumnIds]
  )

  const applyZones = useCallback(
    (
      left: string[],
      middle: string[],
      right: string[],
      visibility: ColumnVisibilityState
    ) => {
      const nextOrder = hasSelectColumn
        ? [SELECT_COLUMN_ID, ...left, ...middle, ...right]
        : [...left, ...middle, ...right]
      setDataColumnPinning(left, right)
      setColumnOrder(nextOrder)
      persistColumnLayout(nextOrder, visibility, left, right)
    },
    [hasSelectColumn, persistColumnLayout, setDataColumnPinning]
  )

  const handleToggleColumn = useCallback(
    (columnId: string) => {
      const col = columns.find((c) => c.id === columnId)
      if (col?.enableHiding === false) return
      setColumnVisibility((prev) => {
        const currentlyVisible = prev[columnId] !== false
        const next = { ...prev, [columnId]: !currentlyVisible }
        persistColumnLayout(columnOrder, next, pinnedLeft, pinnedRight)
        return next
      })
    },
    [columns, columnOrder, persistColumnLayout, pinnedLeft, pinnedRight]
  )

  const applyColumnZones = useCallback(
    (columnId: string, update: (zones: ColumnZones) => ColumnZones) => {
      const zones = splitZones(columnOrder, pinnedLeft, pinnedRight)
      const without = (list: string[]) => list.filter((id) => id !== columnId)
      const next = update({
        left: without(zones.left),
        middle: without(zones.middle),
        right: without(zones.right)
      })
      applyZones(next.left, next.middle, next.right, columnVisibility)
    },
    [
      applyZones,
      columnOrder,
      columnVisibility,
      pinnedLeft,
      pinnedRight,
      splitZones
    ]
  )

  const handlePinChange = useCallback(
    (columnId: string, pin: DataTableColumnPin) => {
      applyColumnZones(columnId, ({ left, middle, right }) => {
        if (pin === 'left') {
          return { left: [...left, columnId], middle, right }
        }
        if (pin === 'right') {
          return { left, middle, right: [...right, columnId] }
        }
        return { left, middle: [...middle, columnId], right }
      })
    },
    [applyColumnZones]
  )

  const handleReorderColumns = useCallback(
    (
      columnId: string,
      toZone: ColumnPickerZone,
      targetColumnId: string,
      edge: 'top' | 'bottom'
    ) => {
      const insertInto = (list: string[]) => {
        const targetIndex = list.indexOf(targetColumnId)
        if (targetIndex === -1) return [...list, columnId]
        const insertIndex = edge === 'bottom' ? targetIndex + 1 : targetIndex
        const next = [...list]
        next.splice(insertIndex, 0, columnId)
        return next
      }

      applyColumnZones(columnId, (zones) => {
        if (toZone === 'left') {
          return { ...zones, left: insertInto(zones.left) }
        }
        if (toZone === 'right') {
          return { ...zones, right: insertInto(zones.right) }
        }
        return { ...zones, middle: insertInto(zones.middle) }
      })
    },
    [applyColumnZones]
  )

  const selectionColumn: ColumnDef<DataTableFeatures, T, unknown> = useMemo(
    () => ({
      id: SELECT_COLUMN_ID,
      meta: { sticky: true },
      header: () => null,
      cell: ({ row }) => (
        <input
          type="checkbox"
          className={checkboxStyle}
          aria-label="Sélectionner la ligne"
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      enableSorting: false,
      enableColumnFilter: false,
      enableResizing: false,
      enableHiding: false,
      size: 40,
      minSize: 40,
      maxSize: 56
    }),
    []
  )

  const allColumns = useMemo(() => {
    if (!hasSelectColumn) return columns
    return [selectionColumn, ...columns]
  }, [selectionColumn, columns, hasSelectColumn])

  const pickerColumns: DataTableColumnPickerItem[] = useMemo(() => {
    const order =
      columnOrder.length > 0
        ? columnOrder.filter((id) => id !== SELECT_COLUMN_ID)
        : dataColumnIds
    const columnsById = new Map(
      columns.filter((c) => c.id).map((c) => [c.id, c])
    )
    const leftSet = new Set(pinnedLeft)
    const rightSet = new Set(pinnedRight)
    return order
      .map((id) => {
        const col = columnsById.get(id)
        if (!col) return null
        const pin: DataTableColumnPin = leftSet.has(id)
          ? 'left'
          : rightSet.has(id)
            ? 'right'
            : false
        return {
          id,
          label: getColumnLabel(col),
          visible: columnVisibility[id] !== false,
          pin,
          hideable: col.enableHiding !== false
        }
      })
      .filter((item): item is DataTableColumnPickerItem => item !== null)
  }, [
    columnOrder,
    columns,
    columnVisibility,
    dataColumnIds,
    pinnedLeft,
    pinnedRight
  ])

  const handleRowClick = useCallback(
    (row: Row<DataTableFeatures, T>) => {
      if (onRowClick) {
        onRowClick(row.original)
      } else {
        row.toggleSelected()
      }
    },
    [onRowClick]
  )

  const pageCount =
    totalItems === 0 ? 1 : Math.ceil(totalItems / pagination.pageSize)

  const handleRowSelectionChange: OnChangeFn<RowSelectionState> = useCallback(
    (updater) => {
      if (!onRowSelectionChange) return
      const next =
        typeof updater === 'function' ? updater(rowSelection) : updater
      onRowSelectionChange(next)
    },
    [onRowSelectionChange, rowSelection]
  )

  const reactTable = useTable({
    features: dataTableFeatures,
    data,
    columns: allColumns,
    state: {
      sorting,
      columnFilters,
      pagination,
      rowSelection,
      columnSizing,
      columnOrder,
      columnVisibility,
      columnPinning,
      expanded
    },
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: handleColumnFiltersChange,
    onPaginationChange: handlePaginationChange,
    onRowSelectionChange: handleRowSelectionChange,
    onColumnSizingChange: handleColumnSizingChange,
    onColumnOrderChange: setColumnOrder,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnPinningChange: setColumnPinning,
    onExpandedChange: setExpanded,
    getRowCanExpand: () => renderExpandedRow !== undefined,
    enableColumnPinning: true,
    defaultColumn: {
      minSize: DEFAULT_COLUMN_MIN,
      size: DATA_TABLE_DEFAULT_COLUMN_SIZE,
      maxSize: DEFAULT_COLUMN_MAX
    },
    pageCount,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    enableRowSelection: true,
    columnResizeMode: 'onChange',
    meta: { onColumnFilterClick: handleColumnFilterClick },
    getRowId
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: reactTable instance is stable; layout deps drive recalculation
  const stickyColumnProps = useMemo(() => {
    const props = new Map<
      string,
      {
        className: string
        style: { width: number; left?: number; right?: number }
      }
    >()

    for (const col of reactTable.getVisibleLeafColumns()) {
      const pinned = col.getIsPinned()
      props.set(col.id, {
        className: clsx(
          pinned === 'start' && stickyCell,
          pinned === 'start' && col.getIsLastColumn('start') && stickyCellEdge,
          pinned === 'end' && stickyCellRight,
          pinned === 'end' && col.getIsFirstColumn('end') && stickyCellRightEdge
        ),
        style: {
          width: col.getSize(),
          left: pinned === 'start' ? col.getStart('start') : undefined,
          right: pinned === 'end' ? col.getAfter('end') : undefined
        }
      })
    }

    return props
  }, [reactTable, columnOrder, columnVisibility, columnSizing, columnPinning])

  const selectedIds = useCallback(
    () => Object.keys(rowSelection),
    [rowSelection]
  )

  const hasSelection = Object.keys(rowSelection).length > 0
  const isEmpty =
    !isLoading && !isFetching && reactTable.getRowModel().rows.length === 0
  const showSkeleton = isLoading && reactTable.getRowModel().rows.length === 0
  const hasColumnFilters = columnFilters.length > 0
  const canResetFilters = showResetFilters ?? hasColumnFilters
  const totalSize = reactTable.getTotalSize()

  return (
    <div className={clsx(themeClass, dataTableRoot)}>
      {isFetching && !showSkeleton && (
        <div className={tableLoadingOverlay}>
          <LoaderIcon />
        </div>
      )}
      <div className={tableFrame}>
        <div ref={headerScrollRef} className={stickyHeaderBar}>
          <div className={stickyHeaderInner} style={{ width: totalSize }}>
            <table className={tableClass} style={{ width: totalSize }}>
              <thead className={thead}>
                {reactTable.getHeaderGroups().map((headerGroup) => (
                  <Fragment key={headerGroup.id}>
                    <tr className={theadTitle}>
                      {headerGroup.headers.map((header) => {
                        const colId = header.column.id
                        const stickyProps = stickyColumnProps.get(colId) ?? {
                          className: '',
                          style: { width: header.getSize() }
                        }
                        const canSort = header.column.getCanSort()
                        const sortDir = header.column.getIsSorted()
                        const canResize = header.column.getCanResize()
                        const meta = header.column.columnDef.meta
                        const alignExtra = cellAlignClass(meta?.cellAlign)

                        return (
                          <th
                            key={header.id}
                            className={clsx(
                              thCell,
                              canSort && thSortable,
                              stickyProps.className,
                              header.id === SELECT_COLUMN_ID && checkboxCell,
                              alignExtra
                            )}
                            style={stickyProps.style}
                            onClick={(e) => {
                              if (canSort) {
                                e.stopPropagation()
                                toggleColumnSort(header.column.id)
                              }
                            }}
                          >
                            <div>
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                              {canSort && (
                                <span
                                  className={thSortIndicator}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    toggleColumnSort(header.column.id)
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={
                                      sortDir === 'asc'
                                        ? faSortUp
                                        : sortDir === 'desc'
                                          ? faSortDown
                                          : faSort
                                    }
                                  />
                                </span>
                              )}
                            </div>
                            {canResize && (
                              <div
                                className={clsx(
                                  resizeHandle,
                                  header.column.getIsResizing() &&
                                    resizeHandleActive
                                )}
                                onMouseDown={(e) => {
                                  e.stopPropagation()
                                  header.getResizeHandler()(e)
                                }}
                                onTouchStart={(e) => {
                                  e.stopPropagation()
                                  header.getResizeHandler()(e)
                                }}
                                onClick={(e) => {
                                  e.stopPropagation()
                                }}
                              />
                            )}
                          </th>
                        )
                      })}
                      {rowActions && (
                        <th className={rowActionsCell} aria-hidden />
                      )}
                    </tr>
                    <tr>
                      {headerGroup.headers.map((header) => {
                        const stickyProps = stickyColumnProps.get(
                          header.column.id
                        ) ?? {
                          className: '',
                          style: { width: header.getSize() }
                        }
                        const meta = header.column.columnDef.meta
                        const alignExtra = cellAlignClass(meta?.cellAlign)
                        const isSelectColumn = header.id === SELECT_COLUMN_ID

                        return (
                          <th
                            key={`${header.id}-filter`}
                            className={clsx(
                              thFilterCell,
                              stickyProps.className,
                              isSelectColumn && checkboxCell,
                              alignExtra
                            )}
                            style={stickyProps.style}
                          >
                            {isSelectColumn ? (
                              <input
                                type="checkbox"
                                className={checkboxStyle}
                                aria-label="Tout sélectionner"
                                checked={reactTable.getIsAllPageRowsSelected()}
                                ref={(el) => {
                                  if (el) {
                                    el.indeterminate =
                                      reactTable.getIsSomePageRowsSelected() &&
                                      !reactTable.getIsAllPageRowsSelected()
                                  }
                                }}
                                onChange={reactTable.getToggleAllPageRowsSelectedHandler()}
                                onClick={(e) => e.stopPropagation()}
                              />
                            ) : (
                              <DataTableHeaderFilter column={header.column} />
                            )}
                          </th>
                        )
                      })}
                      {rowActions && (
                        <th className={rowActionsCell} aria-hidden />
                      )}
                    </tr>
                  </Fragment>
                ))}
              </thead>
            </table>
          </div>
        </div>

        <div
          ref={tableWrapperRef}
          className={clsx(tableWrapper, isPanning && tableWrapperPanning)}
        >
          <table className={tableClass} style={{ width: totalSize }}>
            <tbody className={tbody}>
              {reactTable.getRowModel().rows.map((row) => (
                <Fragment key={row.id}>
                  <tr
                    className={clsx(
                      tr,
                      row.getIsSelected() && trSelected,
                      onRowSelectionChange && trClickable
                    )}
                    onClick={() => handleRowClick(row)}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const colId = cell.column.id
                      const stickyProps = stickyColumnProps.get(colId) ?? {
                        className: '',
                        style: { width: cell.column.getSize() }
                      }
                      const isCheckbox = colId === SELECT_COLUMN_ID
                      const cmeta = cell.column.columnDef.meta
                      return (
                        <td
                          key={cell.id}
                          className={clsx(
                            tdCell,
                            stickyProps.className,
                            isCheckbox && checkboxCell,
                            cmeta?.cellAlign &&
                              cellAlignClass(cmeta?.cellAlign),
                            cmeta?.cellMono && cellMono
                          )}
                          style={stickyProps.style}
                          onClick={
                            isCheckbox ? (e) => e.stopPropagation() : undefined
                          }
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      )
                    })}
                    {rowActions && (
                      <td
                        className={rowActionsCell}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className={rowActionsBar}>
                          {rowActions(row.original)}
                        </div>
                      </td>
                    )}
                  </tr>
                  {renderExpandedRow && row.getIsExpanded() && (
                    <tr className={expandedRow}>
                      <td
                        className={expandedRowCell}
                        colSpan={
                          row.getVisibleCells().length + (rowActions ? 1 : 0)
                        }
                      >
                        <div className={expandedRowContent}>
                          {renderExpandedRow(row.original)}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
              {showSkeleton &&
                Array.from({ length: SKELETON_ROWS_COUNT }, (_, rowIndex) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder rows
                  <tr key={`skeleton-${rowIndex}`} className={tr}>
                    {reactTable
                      .getVisibleLeafColumns()
                      .map((column, colIndex) => {
                        const stickyProps = stickyColumnProps.get(
                          column.id
                        ) ?? {
                          className: '',
                          style: { width: column.getSize() }
                        }
                        const isCheckbox = column.id === SELECT_COLUMN_ID
                        return (
                          <td
                            key={column.id}
                            className={clsx(
                              tdCell,
                              stickyProps.className,
                              isCheckbox && checkboxCell
                            )}
                            style={stickyProps.style}
                          >
                            {!isCheckbox && (
                              <Shimmer
                                height={14}
                                width={
                                  SKELETON_CELL_WIDTHS[
                                    (rowIndex + colIndex) %
                                      SKELETON_CELL_WIDTHS.length
                                  ]
                                }
                              />
                            )}
                          </td>
                        )
                      })}
                    {rowActions && <td className={rowActionsCell} />}
                  </tr>
                ))}
            </tbody>
          </table>
          {isEmpty && (
            <div className={emptyStateOverlay}>
              <NoResult
                message="Aucun résultat"
                onDismiss={canResetFilters ? handleResetFilters : undefined}
                dismissLabel={
                  canResetFilters ? 'Réinitialiser les filtres' : undefined
                }
              />
            </div>
          )}
        </div>
      </div>

      <DataTableStickyScrollbar
        scrollRef={tableWrapperRef}
        barRef={stickyScrollbarRef}
        contentWidth={totalSize}
        onVisibilityChange={setScrollbarVisible}
      />

      <DataTablePagination
        table={reactTable}
        totalItems={totalItems}
        onRefresh={onRefresh}
        isRefreshing={isFetching}
        columnPicker={
          configName ? (
            <DataTableColumnPicker
              columns={pickerColumns}
              onToggle={handleToggleColumn}
              onPinChange={handlePinChange}
              onReorder={handleReorderColumns}
            />
          ) : undefined
        }
      />

      <AnimatePresence>
        {hasSelection && bulkActions && (
          <ActionBar
            text={`${Object.keys(rowSelection).length} sélectionnée(s)`}
            onDismiss={() => onRowSelectionChange?.({})}
          >
            {bulkActions(selectedIds())}
          </ActionBar>
        )}
      </AnimatePresence>
    </div>
  )
}

export default DataTable
export { DataTableFilterableCell } from './DataTableFilterableCell'
export { DataTableRowAction } from './DataTableRowAction'
export { defineColumns } from './defineColumns'
export type {
  DataTableCellContext,
  DataTableColumn,
  DataTableColumnFilter,
  DataTableColumnMeta,
  DataTableQueryParams,
  DataTableRowSelection,
  DataTableSort
} from './types'
export { DATA_TABLE_DEFAULT_COLUMN_SIZE } from './types'
export { useDataTableRowSelection } from './useDataTableRowSelection'
export { useDataTableSearchParams } from './useDataTableSearchParams'
